import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ChevronLeft, Eye, EyeOff, Lock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { extractErrorMessage } from '@/lib/api-errors';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        await api.post('/auth/verify-reset-token', { token });
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('As palavras-passe não coincidem');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('A palavra-passe deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: formData.password
      });
      setResetSuccess(true);
      toast.success('Palavra-passe redefinida com sucesso!');
    } catch (error) {
      const message = extractErrorMessage(error, 'Erro ao redefinir palavra-passe');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-ruby animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">A verificar token...</p>
        </div>
      </div>
    );
  }

  // Invalid/expired token
  if (!tokenValid && !resetSuccess) {
    return (
      <div className="min-h-screen bg-background flex">
        {/* Left - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=1920)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Right - Error Message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Link inválido ou expirado
            </h2>
            
            <p className="text-muted-foreground mb-8">
              Este link de redefinição de palavra-passe é inválido ou já expirou. 
              Os links expiram após 1 hora por motivos de segurança.
            </p>
            
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/forgot-password')}
                data-testid="request-new-link"
                className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold"
              >
                Solicitar novo link
              </Button>
              
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="w-full text-muted-foreground"
              >
                Voltar ao Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-background flex">
        {/* Left - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=1920)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Right - Success Message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Palavra-passe redefinida!
            </h2>
            
            <p className="text-muted-foreground mb-8">
              A tua palavra-passe foi alterada com sucesso. 
              Já pode entrar com a sua nova palavra-passe.
            </p>
            
            <Button
              onClick={() => navigate('/login')}
              data-testid="go-to-login"
              className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold"
            >
              Entrar agora
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-background flex">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-brand text-4xl font-bold text-foreground mb-4">
            Nova palavra-passe
          </h2>
          <p className="text-muted-foreground text-lg max-w-md">
            Escolhe uma palavra-passe segura para proteger a tua conta.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/login')}
            className="mb-8 text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Voltar ao Login
          </Button>

          {/* Logo */}
          <Link to="/" className="block mb-8">
            <img src="/logo-red.png" alt="Intimate.pt" className="h-10 w-auto" />
          </Link>

          <div className="w-14 h-14 bg-ruby/20 rounded-full flex items-center justify-center mb-6">
            <Lock className="w-7 h-7 text-ruby" />
          </div>

          <h2 className="text-2xl font-semibold text-foreground mb-2">
            Criar nova palavra-passe
          </h2>
          <p className="text-muted-foreground mb-8">
            A sua nova palavra-passe deve ter pelo menos 6 caracteres.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password">Nova palavra-passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="reset-password-new"
                  className="bg-surface border-border h-12 pr-12"
                  required
                  minLength={6}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar palavra-passe</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  data-testid="reset-password-confirm"
                  className="bg-surface border-border h-12 pr-12"
                  required
                  minLength={6}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-red-500 text-sm">As palavras-passe não coincidem</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || formData.password !== formData.confirmPassword}
              data-testid="reset-password-submit"
              className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold"
            >
              {loading ? 'A redefinir...' : 'Redefinir palavra-passe'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
